import AspectRatioRoundedIcon from '@mui/icons-material/AspectRatioRounded';
import CropRotateRoundedIcon from '@mui/icons-material/CropRotateRounded';
import FlipRoundedIcon from '@mui/icons-material/FlipRounded';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { imgSizesCalc } from 'utils';
import useFileUtils from '../hooks/useFileUtils';
import ImageCrop from './ImageCrop';
import PdfPreview from './PdfPreview';

const FilePreview = (props) => {
    const { file, aspectRatio = 4 / 5, fixedAspectRatio = false, onSaveCrop } = props;
    const { t } = useTranslation();
    const { cropWidth, cropHeight } = imgSizesCalc(aspectRatio);
    const cropperRef = useRef(null);
    const { handleFilePreview } = useFileUtils();

    const [image, setImage] = useState(null);
    const [pdfLoaded, setPdfLoaded] = useState(false);

    useEffect(() => {
        setPdfLoaded(false);
        if (file?.qubFile instanceof File) {
            setImage(URL.createObjectURL(file?.qubFile));
        } else if (file?.qubFile?.file instanceof File) {
            if (file?.qubFile?.type.startsWith('image')) setImage(URL.createObjectURL(file?.qubFile?.file));
            else console.log('preview for', file);
        } else {
            handleFilePreview(file?.qubFile).then((data) => {
                if (file?.qubFile?.type.startsWith('image')) {
                    setImage(data?.data);
                } else if (file?.qubFile?.type.startsWith('application/pdf')) {
                    const blob = new Blob([data?.data], { type: 'application/pdf' });
                    setImage(blob);
                    setPdfLoaded(true);
                }
            });
        }
    }, [file]);

    const handleSaveCrop = (croppedFile) => {
        if (onSaveCrop && typeof onSaveCrop === 'function') onSaveCrop(file, croppedFile);
    };

    const Previewer = () => {
        if (file?.qubFile?.type.startsWith('image')) {
            return (
                <Stack spacing={1} sx={{ width: '100%', height: '100%' }}>
                    <ImageCrop
                        src={image}
                        srcName={file?.qubFile?.name}
                        fileType={file?.qubFile?.type}
                        defaultAR={aspectRatio}
                        savePhoto={handleSaveCrop}
                        minWidth={cropWidth}
                        minHeight={cropHeight}
                        ref={cropperRef}
                    />
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <Button
                            variant="contained"
                            sx={{ background: '#4d79bc', ':hover': { background: '#79AEC9', color: '#fff' } }}
                            onClick={() => cropperRef?.current?.handleSetRotation()}
                            startIcon={<CropRotateRoundedIcon />}
                        >
                            {t('Rotate')}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ background: '#4d79bc', ':hover': { background: '#79AEC9', color: '#fff' } }}
                            onClick={() => cropperRef?.current?.handleSetFlip()}
                            startIcon={<FlipRoundedIcon />}
                        >
                            {t('Mirror')}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={fixedAspectRatio}
                            sx={{ background: '#4d79bc', ':hover': { background: '#79AEC9', color: '#fff' } }}
                            onClick={(e) => cropperRef?.current?.handleOpenMenu(e)}
                            startIcon={<AspectRatioRoundedIcon />}
                        >
                            {t('AspectRatio')}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ background: '#4d79bc', ':hover': { background: '#79AEC9', color: '#fff' } }}
                            onClick={() => cropperRef?.current?.handleSavePhoto()}
                            startIcon={<SaveIcon />}
                        >
                            {t('SaveImage')}
                        </Button>
                    </Stack>
                </Stack>
            );
        }
        if (file?.qubFile?.type.startsWith('application/pdf')) {
            return <PdfPreview pdf={image} loaded={pdfLoaded} />;
        }
        return <div>No preview available</div>;
    };

    return (
        <Box
            sx={{
                display: 'flex',
                borderRadius: 4,
                flexGrow: 1,
                m: 1
            }}
            justifyContent="center"
            alignItems="flex-start"
        >
            <Previewer />
        </Box>
    );
};

export default FilePreview;
